import React, { useMemo } from'react';
import { useParams, useNavigate } from'react-router-dom';
import { useLayout } from'../components/Layout';
import { useApiQuery } from'../hooks/useApi';
import { useFilters } from'../context/FilterContext';
import api from'../api/client';
import AreaChart from'../components/charts/AreaChart';
import BarChart from'../components/charts/BarChart';
import PieChart from'../components/charts/PieChart';
import DataTable from'../components/tables/DataTable';
import KPICard from'../components/charts/KPICard';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader from'../components/PageLoader';
import { formatTimeRangeLabel } from'../components/TimeRangePicker';
import { formatLatency } from'../utils/format';
import { Activity, Zap, DollarSign, Clock, AlertTriangle, Boxes } from'lucide-react';

export default function ProductDetail() {
 const { id } = useParams();
 const { timeRange, granularity } = useLayout();
 const navigate = useNavigate();
 const { setFilter } = useFilters();

 const { data: product, isPending: productPending } = useApiQuery(
 ['product-detail', id],
 () => api.getProduct(id)
 );

 const { data: analytics, isPending: analyticsPending } = useApiQuery(
 ['product-analytics', id, timeRange, granularity],
 () => api.getProductAnalytics(id, timeRange, granularity),
 { refetchInterval: 30000 }
 );

 const isLoading = productPending || analyticsPending;

 // Gateway-log retention notice — the reliability series is gateway-sourced,
 // so a range longer than retention is clamped by the backend.
 const retentionClamped = !!analytics?.gatewayRetention?.clamped;
 const retentionDays = analytics?.gatewayRetention?.days;

 const tokenData = analytics?.tokens || [];
 const totalCost = tokenData.reduce((s, t) => s + (t.Cost || 0), 0);
 const totalTokens = tokenData.reduce((s, t) => s + (t.TotalTokens || 0), 0);
 const totalCalls = tokenData.reduce((s, t) => s + (t.Calls || 0), 0);

 // Per-subscription usage rolled up from the backend-enriched subscriptions
 // (gateway-log chargeback). Used as a fallback for the KPIs when the
 // token-trace attribution path returns nothing in this deployment.
 const subUsage = useMemo(() => {
 const subs = analytics?.subscriptions || [];
 return subs.reduce(
 (acc, s) => {
 acc.calls += Number(s.calls) || 0;
 acc.tokens += Number(s.tokens) || 0;
 acc.cost += Number(s.cost) || 0;
 return acc;
 },
 { calls: 0, tokens: 0, cost: 0 },
 );
 }, [analytics?.subscriptions]);

 // Cost by model pie data
 const costByModel = {};
 for (const t of tokenData) {
 const m = t.Model ||'unknown';
 costByModel[m] = (costByModel[m] || 0) + (t.Cost || 0);
 }
 const pieData = Object.entries(costByModel).map(([name, value]) => ({
 name,
 value: Math.round(value * 100) / 100,
 }));

 // Flatten time series from all subscriptions
 const flatTimeSeries = (analytics?.timeSeries || []).flatMap((ts) => ts.data);

 // Reliability roll-up from the time series. The backend flags whether the
 // series is gateway-sourced (has real error/latency) via
 // `analytics.hasReliabilitySignal`; the appClientId fallback reports zeros.
 // We trust that flag and, when true, aggregate the real numbers from the
 // series so the KPIs reconcile with the chart.
 const reliability = useMemo(() => {
 const rows = Array.isArray(flatTimeSeries) ? flatTimeSeries : [];
 const hasSignal = !!analytics?.hasReliabilitySignal;
 let calls = 0, errors = 0, latSum = 0, latCount = 0, p95Max = 0;
 for (const r of rows) {
 calls += Number(r?.Calls) || 0;
 errors += Number(r?.Errors) || 0;
 const lat = Number(r?.AvgLatency);
 if (Number.isFinite(lat) && lat > 0) { latSum += lat; latCount++; }
 const p95 = Number(r?.P95Latency);
 if (Number.isFinite(p95) && p95 > p95Max) p95Max = p95;
 }    return {
 hasReliabilitySignal: hasSignal,
 // Real call count from the gateway-log series (has data even when token
 // attribution is empty). Exposed so Total Calls can fall back to it.
 calls,
 errorRate: hasSignal && calls > 0 ? (errors / calls) * 100 : null,
 avgLatency: hasSignal && latCount > 0 ? Math.round(latSum / latCount) : null,
 p95Latency: hasSignal && p95Max > 0 ? p95Max : null,
 };
 }, [flatTimeSeries, analytics?.hasReliabilitySignal]);

 // Subscription columns for table
 const subColumns = [
 { headerName:'Subscription', field:'displayName', flex: 2 },
 { headerName:'State', field:'state', width: 100 },
 {
 headerName:'Calls',
 field:'calls',
 width: 110,
 type:'numericColumn',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 },
 {
 headerName:'Tokens',
 field:'tokens',
 width: 120,
 type:'numericColumn',
 valueFormatter: (p) => {
 const v = Number(p.value) || 0;
 if (v >= 1_000_000) return`${(v / 1_000_000).toFixed(1)}M`;
 if (v >= 1_000) return`${(v / 1_000).toFixed(1)}K`;
 return v.toLocaleString();
 },
 },
 {
 headerName:'Est. Cost',
 field:'cost',
 width: 110,
 type:'numericColumn',
 valueFormatter: (p) => {
 const v = Number(p.value) || 0;
 if (v !== 0 && Math.abs(v) < 0.01) return`$${v.toFixed(4)}`;
 return`$${v.toFixed(2)}`;
 },
 },
 { headerName:'Created', field:'createdDate', width: 130,
 valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString() :'—' },
 ];

 const handleModelPieClick = (entry) => {
 if (entry?.name) {
 setFilter('model', entry.name);
 navigate('/costs');
 }
 };

 const handleSubscriptionClick = (row) => {
 if (row?.displayName || row?.id) {
 navigate(`/conversations?subscriptionId=${encodeURIComponent(row.id || row.displayName)}`);
 }
 };

 return (
 <div className="space-y-6">
 {/* Header */}   <Breadcrumbs items={[
 { label:'Products', href:'/products' },
 { label: product?.displayName || product?.productId || id },
 ]} />   <div>
 <div className="flex items-center gap-2 flex-wrap">
 <h1 className="text-xl font-bold">{product?.displayName || product?.productId || id}</h1>
 {product?.state && (
 <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
 product.state === 'published'
 ? 'bg-emerald-100 text-emerald-700'
 : 'bg-gray-100 text-gray-600'
 }`}>
 {product.state === 'published' ? 'Published' : 'Not published'}
 </span>
 )}
 </div>
 <p className="text-sm text-gray-500 mt-0.5">
 {(product?.apis || []).length} APIs · {(product?.subscriptions || []).length} subscriptions
 <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
 last {formatTimeRangeLabel(timeRange)}
 </span>
 </p>   </div>

 {/* Gateway-log retention notice — shown when the selected range exceeds
 retention, so the truncated reliability series isn't mistaken for a gap. */}
 {retentionClamped && (
 <div
 role="status"
 className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-900"
 >
 <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
 <p className="leading-snug">
 Reliability figures (calls, errors, latency) come from the APIM gateway
 logs, retained for <span className="font-semibold">{retentionDays} days</span>.
 The selected range is longer, so those charts show only the last{' '}
 {retentionDays} days. Token &amp; cost figures are unaffected.
 </p>
 </div>
 )}

 {/* KPIs */}
 {isLoading ? (
 <PageLoader message="Loading product details…" />
 ) : (
 <>     <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
 <ErrorBoundary><KPICard title="Total Calls" value={totalCalls || reliability.calls || subUsage.calls || 0} format="number" icon={Activity} color="brand" help="API calls attributed to this product in the selected range." /></ErrorBoundary>
 <ErrorBoundary><KPICard title="Total Tokens" value={totalTokens || subUsage.tokens || 0} format="tokens" icon={Zap} color="violet" help="Prompt + completion tokens across all model calls for this product's traffic." /></ErrorBoundary>
 <ErrorBoundary><KPICard title="Total Cost" value={totalCost || subUsage.cost || 0} format="currency" icon={DollarSign} color="emerald" help="Estimated spend for this product, priced from its token usage." onClick={() => navigate('/costs')} /></ErrorBoundary>
 <ErrorBoundary>
 <KPICard
 title="Error Rate"
 value={reliability.errorRate}
 format="percent"
 icon={AlertTriangle}
 color={reliability.errorRate != null && reliability.errorRate > 5 ?'red' :'emerald'}
 help={reliability.errorRate != null
 ? "Share of this product's calls that returned an error (HTTP 4xx/5xx) in the selected range."
 : "Error data isn't available for this product in this view (attribution falls back to token traces, which don't carry error signal)."}
 subtitle={reliability.errorRate == null ? 'No error signal' : undefined}
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <KPICard
 title="Avg Latency"
 value={reliability.avgLatency}
 format="ms"
 icon={Clock}
 color="amber"
 help={reliability.avgLatency != null
 ? 'Average end-to-end response time for this product in the selected range.'
 : "Latency data isn't available for this product in this view."}
 subtitle={reliability.p95Latency != null ?`P95: ${formatLatency(reliability.p95Latency)}` : (reliability.avgLatency == null ? 'No latency signal' : undefined)}
 />
 </ErrorBoundary>
 <ErrorBoundary><KPICard title="APIs" value={(product?.apis || []).length} format="number" icon={Boxes} color="brand" help="Number of APIs included in this product." onClick={() => navigate('/apis')} /></ErrorBoundary>
 </div>

 {/* Charts */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ErrorBoundary>
 <AreaChart
 data={flatTimeSeries}
 dataKeys={reliability.hasReliabilitySignal ? ['Calls','Errors'] : ['Calls']}
 seriesLabels={{ Calls:'Total requests', Errors:'Failed requests' }}
 title="Calls Over Time"
 exportFilename={`product-${id}-calls`}
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <PieChart
 data={pieData}
 title="Cost by Model"
 exportFilename={`product-${id}-cost-model`}
 height={280}
 onSliceClick={handleModelPieClick}
 />
 </ErrorBoundary>
 </div>

 {/* Token Breakdown */}
 {tokenData.length > 0 && (
 <ErrorBoundary>
 <BarChart
 data={tokenData.map((t) => ({
 name: t.Model,
'Prompt Tokens': t.PromptTokens,
'Completion Tokens': t.CompletionTokens,
 }))}
 dataKeys={['Prompt Tokens','Completion Tokens']}
 xAxisKey="name"
 title="Token Usage by Model"
 stacked
 height={250}
 exportFilename={`product-${id}-tokens`}
 />
 </ErrorBoundary>
 )}

 {/* Subscriptions Table */}
 <ErrorBoundary>  <DataTable
 data={analytics?.subscriptions || product?.subscriptions || []}
 columns={subColumns}
 title="Subscriptions"
 height={300}
 exportFilename={`product-${id}-subscriptions`}
 onRowClick={handleSubscriptionClick}
 />
 </ErrorBoundary>
 </>
 )}
 </div>
 );
}
